import { Journals } from '../../../../types/Journals.types';

/** An empty list, or a value that's missing or only whitespace. */
export function isMissingColumn(
  journal: Journals,
  key: keyof Journals
): boolean {
  const value = journal[key];
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === 'string' ? !value.trim() : !value;
}

const MANDATORY_KEYS: (keyof Journals)[] = [
  'name',
  'publishing_model',
  'licenses',
  'payment',
  'url',
];

export function isMissingData(journal: Journals): boolean {
  return MANDATORY_KEYS.some((key) => isMissingColumn(journal, key));
}

// The journal cell's badge covers a missing name or url.
export type MissingDataKey =
  'name' | 'payment' | 'licenses' | 'publishing_model';

/** What the journal cell's badge reports: a missing name first, else a missing url. */
export function journalCellMissing(
  journal: Journals
): 'name' | 'url' | undefined {
  if (isMissingColumn(journal, 'name')) return 'name';
  if (isMissingColumn(journal, 'url')) return 'url';
  return undefined;
}

export function missingDataKeys(journal: Journals): MissingDataKey[] {
  const keys: MissingDataKey[] = [];
  if (journalCellMissing(journal)) keys.push('name');
  if (isMissingColumn(journal, 'payment')) keys.push('payment');
  if (isMissingColumn(journal, 'licenses')) keys.push('licenses');
  if (isMissingColumn(journal, 'publishing_model')) {
    keys.push('publishing_model');
  }
  return keys;
}

// Ids that let a row or card's aria-describedby point at its status and missing-data text.
// The desktop table and the mobile cards are both mounted (one hidden by CSS), so each view
// needs its own ids.
export const journalRowId = (
  view: 'desktop' | 'mobile',
  journal: Journals,
  index: number
) => `journal-${view}-${journal._id ?? index}`;
export const missingDataId = (rowId: string, key: MissingDataKey) =>
  `${rowId}-missing-${key}`;
export const masterlistStatusId = (rowId: string) => `${rowId}-masterlist`;

/**
 * aria-describedby for a row or card: its aria-label replaces the content for screen readers,
 * so point at the masterlist status and the missing-data text explicitly.
 */
export function journalDescribedBy(
  rowId: string,
  journal: Journals,
  hasCheck: boolean
): string | undefined {
  const ids = [
    ...(hasCheck ? [masterlistStatusId(rowId)] : []),
    ...missingDataKeys(journal).map((key) => missingDataId(rowId, key)),
  ];
  return ids.length ? ids.join(' ') : undefined;
}
